import { z } from "zod";

import { quantityQualifierSchema } from "./observation.js";
import { serviceCategorySchema } from "./service.js";
import { calendarDateSchema, isoTimestampSchema } from "./timing.js";

export const serviceIntentStatusSchema = z.enum([
  "active",
  "committed",
  "deferred",
  "lost",
  "cancelled",
]);
export type ServiceIntentStatus = z.infer<typeof serviceIntentStatusSchema>;

export const serviceIntentRequestKindSchema = z.enum([
  "quote",
  "pricing",
  "vendor_setup",
  "scheduling",
  "perform_service",
]);
export type ServiceIntentRequestKind = z.infer<
  typeof serviceIntentRequestKindSchema
>;

export const serviceIntentQuoteStatusSchema = z.enum([
  "not_requested",
  "requested",
  "sent",
]);
export type ServiceIntentQuoteStatus = z.infer<
  typeof serviceIntentQuoteStatusSchema
>;

export const serviceIntentPurchasingStatusSchema = z.enum([
  "not_assessed",
  "required",
  "complete",
  "not_required",
]);
export type ServiceIntentPurchasingStatus = z.infer<
  typeof serviceIntentPurchasingStatusSchema
>;

export const serviceIntentEvidenceSourceSchema = z.enum([
  "customer_statement",
  "salesperson_commitment",
  "exactpro_action",
]);
export type ServiceIntentEvidenceSource = z.infer<
  typeof serviceIntentEvidenceSourceSchema
>;

export const serviceIntentTargetWindowSchema = z.discriminatedUnion(
  "precision",
  [
    z
      .object({
        precision: z.literal("month"),
        yearMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u),
      })
      .strict(),
    z
      .object({
        precision: z.literal("exact_date"),
        date: calendarDateSchema,
      })
      .strict(),
  ],
);
export type ServiceIntentTargetWindow = z.infer<
  typeof serviceIntentTargetWindowSchema
>;

export const serviceIntentScopeItemSchema = z
  .object({
    serviceCategory: serviceCategorySchema,
    expectedQuantity: z
      .object({
        value: z.number().int().min(1).max(100_000),
        qualifier: quantityQualifierSchema,
      })
      .strict()
      .optional(),
  })
  .strict();
export type ServiceIntentScopeItem = z.infer<
  typeof serviceIntentScopeItemSchema
>;

export const serviceIntentScopeSchema = z
  .array(serviceIntentScopeItemSchema)
  .min(1)
  .max(2)
  .superRefine((scope, context) => {
    const categories = new Set<string>();
    for (const [index, item] of scope.entries()) {
      if (categories.has(item.serviceCategory)) {
        context.addIssue({
          code: "custom",
          path: [index, "serviceCategory"],
          message:
            "A Service Intent can include each service category only once.",
        });
      }
      categories.add(item.serviceCategory);
    }
  });
export type ServiceIntentScope = z.infer<typeof serviceIntentScopeSchema>;

export const purchasingRequirementKindSchema = z.enum([
  "w9",
  "certificate_of_insurance",
  "vendor_registration",
  "purchase_order",
  "other",
]);
export type PurchasingRequirementKind = z.infer<
  typeof purchasingRequirementKindSchema
>;

export const purchasingRequirementPartySchema = z.enum([
  "customer",
  "exact_pro",
]);
export type PurchasingRequirementParty = z.infer<
  typeof purchasingRequirementPartySchema
>;

export const purchasingRequirementStatusSchema = z.enum(["open", "satisfied"]);
export type PurchasingRequirementStatus = z.infer<
  typeof purchasingRequirementStatusSchema
>;

const serviceIntentId = z.uuid();

export const serviceIntentChangeActionSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("create_intent"),
      requestKind: serviceIntentRequestKindSchema,
      scope: serviceIntentScopeSchema,
      targetWindow: serviceIntentTargetWindowSchema.optional(),
      requesterContactId: z.uuid().optional(),
      evidenceSource: z.literal("customer_statement"),
    })
    .strict(),
  z
    .object({
      action: z.literal("mark_quote_sent"),
      serviceIntentId,
      evidenceSource: z.literal("exactpro_action"),
    })
    .strict(),
  z
    .object({
      action: z.literal("record_commitment"),
      serviceIntentId,
      evidenceSource: z.literal("customer_statement"),
    })
    .strict(),
  z
    .object({
      action: z.literal("add_purchasing_requirement"),
      serviceIntentId,
      kind: purchasingRequirementKindSchema,
      responsibleParty: purchasingRequirementPartySchema,
      initialStatus: purchasingRequirementStatusSchema.default("open"),
      description: z.string().trim().max(500).optional(),
      evidenceSource: serviceIntentEvidenceSourceSchema,
    })
    .strict(),
  z
    .object({
      action: z.literal("satisfy_purchasing_requirement"),
      serviceIntentId,
      requirementId: z.uuid(),
      evidenceSource: serviceIntentEvidenceSourceSchema,
    })
    .strict(),
  z
    .object({
      action: z.literal("confirm_purchasing_complete"),
      serviceIntentId,
      evidenceSource: z.enum(["customer_statement", "salesperson_commitment"]),
    })
    .strict(),
  z
    .object({
      action: z.literal("confirm_purchasing_not_required"),
      serviceIntentId,
      evidenceSource: z.enum(["customer_statement", "salesperson_commitment"]),
    })
    .strict(),
  z
    .object({
      action: z.literal("update_target_window"),
      serviceIntentId,
      targetWindow: serviceIntentTargetWindowSchema,
      evidenceSource: z.literal("customer_statement"),
    })
    .strict(),
  z
    .object({
      action: z.literal("defer_intent"),
      serviceIntentId,
      targetWindow: serviceIntentTargetWindowSchema,
      evidenceSource: z.literal("customer_statement"),
    })
    .strict(),
  z
    .object({
      action: z.literal("mark_intent_lost"),
      serviceIntentId,
      evidenceSource: z.literal("customer_statement"),
    })
    .strict(),
  z
    .object({
      action: z.literal("cancel_intent"),
      serviceIntentId,
      evidenceSource: z.literal("customer_statement"),
    })
    .strict(),
]);
export type ServiceIntentChangeAction = z.infer<
  typeof serviceIntentChangeActionSchema
>;

export const followUpStatusSchema = z.enum(["open", "completed", "cancelled"]);
export type FollowUpStatus = z.infer<typeof followUpStatusSchema>;

export const followUpActionSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("create"),
      dueDate: calendarDateSchema,
      dueAt: isoTimestampSchema.optional(),
      description: z.string().trim().min(1).max(240),
    })
    .strict(),
  z
    .object({
      action: z.literal("reschedule"),
      followUpId: z.uuid(),
      dueDate: calendarDateSchema,
      dueAt: isoTimestampSchema.optional(),
    })
    .strict(),
  z.object({ action: z.literal("complete"), followUpId: z.uuid() }).strict(),
  z.object({ action: z.literal("cancel"), followUpId: z.uuid() }).strict(),
]);
export type FollowUpAction = z.infer<typeof followUpActionSchema>;

export const serviceIntentSummarySchema = z
  .object({
    id: z.uuid(),
    labId: z.uuid(),
    requesterContactId: z.uuid().nullable(),
    requestKind: serviceIntentRequestKindSchema,
    status: serviceIntentStatusSchema,
    quoteStatus: serviceIntentQuoteStatusSchema,
    purchasingStatus: serviceIntentPurchasingStatusSchema,
    scope: serviceIntentScopeSchema,
    targetWindow: serviceIntentTargetWindowSchema.nullable(),
    openRequirementCount: z.number().int().min(0).max(100),
    readyForOperations: z.boolean(),
    updatedAt: isoTimestampSchema,
  })
  .strict();
export type ServiceIntentSummary = z.infer<typeof serviceIntentSummarySchema>;

export const purchasingRequirementSummarySchema = z
  .object({
    id: z.uuid(),
    kind: purchasingRequirementKindSchema,
    responsibleParty: purchasingRequirementPartySchema,
    status: purchasingRequirementStatusSchema,
    description: z.string().max(500),
    createdAt: isoTimestampSchema,
    satisfiedAt: isoTimestampSchema.nullable(),
  })
  .strict();
export type PurchasingRequirementSummary = z.infer<
  typeof purchasingRequirementSummarySchema
>;

export const followUpSummarySchema = z
  .object({
    id: z.uuid(),
    labId: z.uuid().nullable(),
    contactId: z.uuid().nullable(),
    contextLocationId: z.uuid().nullable(),
    ownerUserId: z.uuid(),
    dueDate: calendarDateSchema,
    dueAt: isoTimestampSchema.nullable(),
    description: z.string().trim().min(1).max(240),
    status: followUpStatusSchema,
    createdAt: isoTimestampSchema,
  })
  .strict();
export type FollowUpSummary = z.infer<typeof followUpSummarySchema>;
